import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import "./index.css";

const PopupMessage = ({
    message,
    popupTrigger,
    duration = 5000,
    onClose,
    children,
    variant = "default",
    contentClassName = "",
    closable = true,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const { t } = useTranslation();

    const hasRenderableContent = useMemo(
        () => Boolean(children) || Boolean(message),
        [children, message]
    );

    useEffect(() => {
        if (!hasRenderableContent) {
            setIsVisible(false);
            return;
        }
        setIsVisible(true);
        setIsFadingOut(false);
    }, [popupTrigger, hasRenderableContent]);

    const handleClose = useCallback(() => {
        setIsFadingOut(true);
    }, []);

    useEffect(() => {
        if (!duration || !isVisible || isFadingOut) {
            return () => {};
        }
        const timer = setTimeout(() => handleClose(), duration);
        return () => clearTimeout(timer);
    }, [duration, handleClose, isFadingOut, isVisible]);

    const handleAnimationEnd = useCallback(() => {
        if (!isFadingOut) {
            return;
        }
        setIsVisible(false);
        setIsFadingOut(false);
        if (typeof onClose === "function") {
            onClose();
        }
    }, [isFadingOut, onClose]);

    if (!hasRenderableContent || !isVisible) {
        return null;
    }

    const overlayClassName = variant === "chat"
        ? "popup-overlay popup-overlay--chat"
        : "popup-overlay";

    return (
        <div className={overlayClassName} role="status" aria-live="polite">
            <div
                className={`popup-content popup-content--${variant} ${contentClassName} ${isFadingOut ? "fade-out" : ""}`.trim()}
                onAnimationEnd={handleAnimationEnd}
            >
                <div className="popup-body">
                    {children || <div className="popup-message">{message}</div>}
                </div>
                {closable && (
                    <button className="popup-close" onClick={handleClose} type="button" aria-label={t("notifications.closeAria")}>
                        &times;
                    </button>
                )}
            </div>
        </div>
    );
};

PopupMessage.propTypes = {
    message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    popupTrigger: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    duration: PropTypes.number,
    onClose: PropTypes.func,
    children: PropTypes.node,
    variant: PropTypes.string,
    contentClassName: PropTypes.string,
    closable: PropTypes.bool,
};

export default PopupMessage;
